import { ARC_EVENTS, VALUE_EVENTS, BAR_IMAGE_SOURCE_STYLE, MAIN_STYLE, KNOB_STYLE, MAIN_STYLE_GROUPS, LVGL_STYLE_INT_MIN, LVGL_STYLE_INT_MAX, SIGNED_PADDING_SIDES } from '../shared.js'
export const arc = {

    // LVGL-style parts — label/exportSuffix/order are the source of truth for
    // the Property Editor and for legacy JSONL wire keys (see v2/shared.js
    // part metadata doc and v2/wireFormat.js toWireKey).
    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                // LVGL 7's Arc BG is drawn through lv_draw_rect(), so its
                // pattern image is the same img_src resource used by Bar.
                ...BAR_IMAGE_SOURCE_STYLE,
                line_width:   { type: 'int',  min: 0 },
                line_color:   { type: 'color' },
                line_rounded: { type: 'bool' },
                padding:      { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, exportSpecial: true },
                ...SIGNED_PADDING_SIDES,
            },
        },
        indicator: {
            label: 'Indicator',
            exportSuffix: '10',
            order: 1,
            properties: {
                line_width:    { type: 'int',  min: 0 },
                line_color:    { type: 'color' },
                line_rounded:  { type: 'bool' },
                // lv_arc_design reads pad_left/right/top/bottom on LV_ARC_PART_INDIC to inset
                // the indicator ring's radius (LV_MATH_MAX4 of the four). Not the 4-side
                // "padding" exportSpecial shorthand — ObjectExport.js only special-cases that
                // for the 'main'/'knob'/'item_buttons' part names, so a non-main/knob part would
                // be silently dropped on export; exposed here as the real per-side LVGL style
                // properties instead, exported generically like any other style property.
                ...SIGNED_PADDING_SIDES,
            },
        },
        knob: {
            label: 'Knob',
            exportSuffix: '20',
            order: 2,
            properties: {
                ...KNOB_STYLE,
                // LVGL 7's knob is also a rect descriptor and therefore uses
                // the same pattern-image source as the BG part.
                ...BAR_IMAGE_SOURCE_STYLE,
                // Legacy Arc knob.padding is the total extra size. New edits
                // use raw LVGL per-side pads, exactly like Slider/Switch.
                padding: { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, exportSpecial: true },
                ...SIGNED_PADDING_SIDES,
                // Not added to the shared KNOB_STYLE constant (v2/shared.js) — arc is
                // currently its only consumer, but adding it directly here keeps the change's
                // blast radius contained to arc regardless. lv_arc_design draws the knob via a
                // full rect descriptor including radius; HaspArc.js mirrors that descriptor
                // with the shared LVGL-inside-border CustomRect.
                radius: { type: 'int', min: 0, max: 999 },
            },
            paddingSidesAuthoritative: true,
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid: { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        val: { type: 'int' }, min: { type: 'int' }, max: { type: 'int' },
        start_value: { type: 'int', visibleWhen: { property: 'type', equals: 3 } },
        angle:       { type: 'int', min: 0, max: 360, unit: '°', hasp: true },
        rotation:    { type: 'int', min: 0, max: 360, unit: '°', hasp: true },
        start_angle: { type: 'int', min: 0, max: 360, unit: '°' },
        end_angle:   { type: 'int', min: 0, max: 360, unit: '°' },
        // Value-arc angle range — ATTR_START_ANGLE1/ATTR_END_ANGLE1 →
        // lv_arc_set_start_angle/lv_arc_set_end_angle (hasp_attribute.cpp:1967-1968), distinct
        // from the BACKGROUND arc's start_angle/end_angle above. Previously unexposed; the gap
        // was already self-documented in HaspArc.js's header comment ("the firmware also
        // supports separate start/end angles for the indicator arc (not exposed by this
        // schema/widget yet)"). Schema-only for now — HaspArc.js doesn't read these yet, so
        // they have no rendering effect in the designer canvas until that widget-code work
        // lands (deferred, same as the rest of arc's Phase 4 migration).
        indicator_start_angle: { type: 'int', min: 0, max: 360, unit: '°' },
        indicator_end_angle:   { type: 'int', min: 0, max: 360, unit: '°' },
        adjustable:  { type: 'bool' },
        // Arc's public order is deliberately not OPENHASP_CONTROL_TYPE's order:
        // 1 is Symmetrical and 2 is Reverse for lv_arc.
        type:        { type: 'enum', values: [0, 1, 2, 3], label: 'arc type' },
        value_str:   { type: 'string' },
        value_font:  { type: 'int', min: 8 },
        // Value Font Family selection — same VALUE_FONT-slot convention as
        // gauge.js/linemeter.js's identically-named property (see gauge.js's
        // comment for the full wire-format semantics). Falls back to the
        // hardcoded 'Material Icons' family when unset, same as those widgets.
        value_font_resource: { type: 'resource', resourceType: 'font', label: 'Value Font Family', inline: true },
        value_color: { type: 'color' },
        value_ofs_x: { type: 'int', label: 'value offset x' },
        value_ofs_y: { type: 'int', label: 'value offset y' },
        entityid:    { type: 'string', internal: true },
        stretch_mode: {
            type: 'string', internal: true, default: 'keep_ratio',
            options: ['none', 'keep_ratio', 'v_stretch', 'h_stretch', 'full'],
        },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 100, height: 100, min_width: 100, min_height: 10,
            enabled: true, hidden: false,
            min: 0, max: 100, val: 0, start_value: 0, value_str: '',
            rotation: 0, start_angle: 135, end_angle: 45,
            // Real LVGL default (lv_arc_create, lv_arc.c:84-85): arc_angle_start = 135,
            // arc_angle_end = 270 — matches the background's own 135/45 start but a different
            // end, since the value arc's extent is normally driven by val/min/max instead.
            indicator_start_angle: 135, indicator_end_angle: 270,
            adjustable: false, type: 0,
            // value_font has no entry in any theme file (unlike gauge/linemeter,
            // which both set value_font: 32 identically in every theme) — same
            // missing-default bug as line_rounded below, so notDefault could
            // never be true regardless of the value set.
            value_font: 32, value_ofs_x: 0, value_ofs_y: 0,
            value_color: "#ffffff",
            value_font_resource: '',
        },
        // Part defaults — ensure knob renders even when a theme omits them.
        // line_rounded both default to true, matching openHASP firmware's
        // actual default. Declared here (not left to theme files alone) so
        // ObjectExport's notDefault check (effectiveDefault !== undefined &&
        // ...) always has a value to compare against, for every theme.
        styles: {
            // line_width 25 (both parts) and main.border_width 25 match the real theme
            // (HASP_DPX(25), lv_theme_hasp.c:620-648: arc_bg's line_width/border_width and
            // arc_indic's line_width are all HASP_DPX(25) at the reference DPI). Previously 15
            // and unset, respectively.
            main:      { line_width: 25, padding: 20, line_rounded: true, border_width: 25 },
            indicator: { line_width: 25, line_rounded: true, pad_left: 0, pad_right: 0, pad_top: 0, pad_bottom: 0 },
            // radius: 999 (circle) — the theme forces LV_RADIUS_CIRCLE on the knob by default;
            // matches the "999 == circular" convention used elsewhere in this codebase (e.g.
            // gauge/slider indicator radius). Not currently read by HaspArc.js's own knob
            // rendering (see the property comment above) — schema-only for now.
            knob:      { padding: 0, border_width: 0, radius: 999 },
        },
    },
    states: ['pressed', 'disabled'],
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu', 'keep_ratio'],
    runtimeCapabilities: ['has_action', 'has_data'],

    events: ARC_EVENTS,
    // An adjustable arc runs the firmware slider handler: no long/hold/release.
    eventVariants: [{ when: { adjustable: true }, events: VALUE_EVENTS }],

    bindings: {
        properties: { val: {}, min: {}, max: {}, start_value: {}, value_str: {}, indicator: { line_color: {} } },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'light', 'climate', 'cover', 'esphome'] },
    },

    automation: {
        triggerLabels: { up: 'Released' },
        preferredDataParameters: {
            'light.turn_on': ['brightness', 'brightness_pct'],
        },
        runtimeVariables: [
            { name: 'val', type: 'int', label: 'Arc Value' },
            { name: 'start', type: 'int', label: 'Arc Range Start' },
        ],
        actionDomains: ['light', 'cover', 'climate', 'homeassistant', 'openhasp', 'esphome'],
    },
}
